import { cloneDeep } from 'lodash';
import { useRef } from 'react';
import { getFramework, getTargetType, useIdentity, useMoveEventListener } from '@/framework';
import { ITouchKitEvent } from '@/utils/event-kit';
import { ILayerProps, IPoint } from '@/version-types';
import { CropStagePlugin } from '../controller';
import { useStageCropStore } from '../models/crop-stage-state';

export const useMoveOperate = (scale: number) => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const originIndex = framework.stage.getActiveOriginIndex();
    const getLayers = () => framework.getLayers(originIndex);
    const getMainLayer = () => framework.getMainLayer(originIndex)!;
    const cropController = framework.getInstance(CropStagePlugin);

    const setIsCropStageOperating = useStageCropStore()((state) => state.setIsCropStageOperating);
    const lastMainLayerRef = useRef<ILayerProps | null>(null);
    const lastLayersRef = useRef<ILayerProps[] | null>(null);
    const lastPointRef = useRef<IPoint>({ x: 0, y: 0 });

    useMoveEventListener((e: ITouchKitEvent) => {
        const target = e.target;
        const dataType = target.getAttribute('data-type');

        if (!getTargetType(target)) return;

        if (dataType !== 'move') return;

        if (e.isStart) {
            const mainLayer = getMainLayer();
            const layers = getLayers();
            lastMainLayerRef.current = cloneDeep(mainLayer);
            lastLayersRef.current = cloneDeep(layers);
            lastPointRef.current = { x: e.startPoint.x, y: e.startPoint.y };
            return;
        }

        if (!lastMainLayerRef.current || !lastLayersRef.current) {
            return;
        }

        const { newMainLayer, newLayers, newPoint } = cropController.moveStageOperate(
            lastPointRef.current,
            {
                x: e.deltaX,
                y: e.deltaY,
            },
            lastMainLayerRef.current,
            lastLayersRef.current,
            scale,
        );

        lastMainLayerRef.current = newMainLayer;
        lastLayersRef.current = newLayers;
        lastPointRef.current = newPoint;

        if (e.isEnd) {
            setIsCropStageOperating(false);
            lastMainLayerRef.current = null;
            lastLayersRef.current = null;
        }
    });
};
